@use '../../../../scss/utility';

@use 'modal-level-vars';

:host {
  @include modal-level-vars.wb-modal-level-vars;

  @include utility.breakpoint-from(mq4) {
    @include modal-level-vars.wb-modal-level-desktop-vars;
  }

  display: block;
  overflow: hidden;
  min-height: 0;
}

.inner {
  display: none;
  overflow: hidden;

  &.is-visible {
    display: flex;
    flex-direction: column;
    max-height: 100vh;
  }

  &.is-entering {
    position: absolute;
    display: block;
    width: 100%;
  }

  @include utility.breakpoint-from(mq4) {
    &.has-footer {
      max-height: 85vh;
    }
  }
}

.is-entering,
.is-leaving {
  .content,
  .footer {
    opacity: 0;
  }
}

.is-full-height {
  height: 100dvh;

  @include utility.breakpoint-from(mq4) {
    height: auto;
  }
}

.height-transition {
  transition: height var(--modal-animation-duration) var(--wb-move);
}

.header {
  display: flex;
  align-items: center;
  border-block-end: var(--modal-border-weight) solid var(--modal-border-color);
  padding-block: var(--modal-level-header-padding-block);
  padding-inline: var(--modal-level-header-padding-inline);
  box-sizing: border-box;
  flex-shrink: 0;

  &.is-hidden {
    transform: translateY(-100%);
  }
  &.is-visible {
    transform: translateY(0%);
  }
}

.footer {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  justify-content: end;
  border-block-start: var(--modal-border-weight) solid var(--modal-border-color);
  padding-block: var(--modal-level-footer-padding-block);
  padding-inline: var(--modal-level-footer-padding-inline);
  gap: var(--modal-content-footer-actions-gap);
  box-sizing: border-box;
  flex-shrink: 0;

  &.is-hidden {
    transform: translateY(100%);
  }
  &.is-visible {
    transform: translateY(0%);
  }
}

:host(:not([size='s'])) .footer {
  @include utility.breakpoint-from(mq4) {
    flex-direction: row;
  }
}

.close-button {
  margin-inline-start: auto;
}

.header,
.footer {
  transition: transform var(--modal-animation-duration) var(--wb-fade);
}

.content {
  color: var(--modal-text-body-color);
  display: block;
  overflow: auto;
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 0;

  // Fix for Firefox padding
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: var(--content-after-height);
  }

  &.fade-in {
    animation: wbModalFadeIn var(--modal-animation-duration) var(--wb-fade) forwards;
  }
  &.fade-out {
    animation: wbModalFadeOut var(--modal-animation-duration) var(--wb-fade) forwards;
  }
}

.headline {
  @include utility.wb-spacing('margin-block-end', 'xxs');
  display: block;
  color: var(--modal-text-headline-color);
  margin-block-start: 0;
}

:host([level='3']) {
  content {
    &.fade-out {
      animation: wbModalFadeOutToLeft var(--modal-animation-duration) var(--wb-fade);
    }
    &.fade-in {
      animation: wbModalFadeInFromRight var(--modal-animation-duration) var(--wb-fade);
    }
  }
}

:host([is-rtl][level='3']) {
  .content {
    &.fade-out {
      animation: wbModalFadeOutToRight var(--modal-animation-duration) var(--wb-fade);
    }
    &.fade-in {
      animation: wbModalFadeInFromLeft var(--modal-animation-duration) var(--wb-fade);
    }
  }
}

:host([variant='edit']),
:host([variant='edit-mobile-fullscreen']) {
  .content {
    @include utility.wb-spacing('padding', 'xs xs 0 xs');

    @include utility.breakpoint-from(mq4) {
      @include utility.wb-spacing('padding', 's xs 0 xs');
      max-height: var(--modal-level-edit-content-max-height);
    }

    &::after {
      height: var(--wb-spacing-m);
    }
  }

  .has-footer {
    .content {
      max-height: var(--modal-level-edit-content-max-height);
    }
  }

  .footer {
    inset-block-end: 0;
    width: 100%;
    position: relative;
  }
}

:host([variant='edit-mobile-fullscreen']) {
  @include utility.breakpoint-to(mq3) {
    /* make sure footer sticks to the bottom */
    /* and use dynamic viewport units to adapt on mobile */
    .inner {
      height: 100dvh;
    }

    .footer {
      margin-block-start: auto;
    }

    .has-footer {
      .content {
        max-height: 100%;
      }
    }
  }
}

:host([variant='info']) {
  .content {
    height: 100%;
    @include utility.wb-spacing('padding', 's xs 0 xs');
    overflow: auto;

    &::after {
      height: var(--wb-spacing-l);
    }
  }
}

:host([variant='prompt']) {
  .content {
    @include utility.wb-spacing('padding', 'xs xs 0 xs');

    &::after {
      height: var(--wb-spacing-xs);
    }
  }
}

@keyframes wbModalSlideToRight {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(40px);
  }
}

@keyframes wbModalFadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes wbModalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes wbModalFadeOutToLeft {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-40px);
  }
}

@keyframes wbModalFadeOutToRight {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(40px);
  }
}

@keyframes wbModalFadeInFromRight {
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0px);
  }
}

@keyframes wbModalFadeInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateY(0px);
  }
}
